import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ApiClientService from '../services/api-client.service';

export enum RequestStatus {
  idle = 'idle',
  loading = 'loading',
  success = 'success',
  failed = 'failed',
  cancelled = 'cancelled',
}

export enum RequestErrorKind {
  http = 'http',
  network = 'network',
  client = 'client',
}

export type RequestError = { kind: RequestErrorKind.http; statusCode: number } | { kind: RequestErrorKind.network | RequestErrorKind.client; statusCode?: undefined };

/** Data exists only on success; starting a request clears previous data and errors. */
export type RequestState<T> =
  | { status: RequestStatus.idle | RequestStatus.loading; data: undefined; error: undefined }
  | { status: RequestStatus.success; data: T; error: undefined }
  | { status: RequestStatus.failed; data: undefined; error: RequestError };

/** Awaited execute() outcome; request failures are returned rather than thrown. */
export type RequestResult<T> = { status: RequestStatus.success; data: T } | { status: RequestStatus.failed; error: RequestError } | { status: RequestStatus.cancelled };

interface IFetchOptions {
  /** Missing or empty URLs leave the hook idle. */
  url: string | undefined;
  /** Defaults to true; enabling starts a fresh GET, disabling clears its result. */
  enabled?: boolean;
  /** Defaults to false; enable only on protected pages where reloading starts sign-in. */
  reloadOnUnauthorized?: boolean;
}

interface IRequestOptions<TInput, TData> {
  /** Use ApiClientService for CSRF/error handling and forward the signal to the transport. */
  request: (input: TInput, signal: AbortSignal) => Promise<TData>;
  /** Defaults to false; enable only on protected pages where reloading starts sign-in. */
  reloadOnUnauthorized?: boolean;
}

const idleState = { status: RequestStatus.idle, data: undefined, error: undefined } as const;
const loadingState = { status: RequestStatus.loading, data: undefined, error: undefined } as const;

function requestErrorFromUnknown(error: unknown): RequestError {
  if (typeof error === 'object' && error !== null) {
    if (
      (error instanceof Error || ('kind' in error && error.kind === RequestErrorKind.http)) &&
      'statusCode' in error &&
      typeof error.statusCode === 'number' &&
      Number.isInteger(error.statusCode) &&
      error.statusCode >= 100 &&
      error.statusCode <= 599
    )
      return { kind: RequestErrorKind.http, statusCode: error.statusCode };
    if ((error instanceof Error && error.name === 'NetworkError') || ('kind' in error && error.kind === RequestErrorKind.network)) return { kind: RequestErrorKind.network };
  }
  return { kind: RequestErrorKind.client };
}

function handleUnauthorized(error: RequestError, reloadOnUnauthorized: boolean): void {
  if (reloadOnUnauthorized && error.statusCode === 401) window.location.reload();
}

/** Automatic independent GETs with component-local results, no shared cache or automatic retries. */
export function useFetch<T>({ url, enabled = true, reloadOnUnauthorized = false }: IFetchOptions) {
  const [request, setRequest] = useState({ url, enabled, reloadOnUnauthorized });
  const [result, setResult] = useState<{ request: typeof request; state: RequestState<T> }>();
  const controllerRef = useRef<AbortController | undefined>(undefined);

  // Reset during render so changed options cannot commit a render containing the previous result.
  if (request.url !== url || request.enabled !== enabled || request.reloadOnUnauthorized !== reloadOnUnauthorized) {
    setRequest({ url, enabled, reloadOnUnauthorized });
    setResult(undefined);
  }

  useEffect(() => {
    const requestUrl = request.url;
    if (!request.enabled || !requestUrl) return;
    const controller = new AbortController();
    controllerRef.current = controller;

    async function fetchData(url: string) {
      try {
        const data = await ApiClientService.get<T>(url, controller.signal);
        // The transport may finish despite cancellation, including during Strict Mode effect replay.
        if (!controller.signal.aborted) setResult({ request, state: { status: RequestStatus.success, data, error: undefined } });
      } catch (error) {
        if (controller.signal.aborted) return;
        const normalizedError = requestErrorFromUnknown(error);
        setResult({ request, state: { status: RequestStatus.failed, data: undefined, error: normalizedError } });
        handleUnauthorized(normalizedError, request.reloadOnUnauthorized);
      }
    }

    void fetchData(requestUrl);
    return () => controller.abort();
  }, [request]);

  /** Restarts an enabled GET; a new request identity hides results from the previous attempt. */
  const refetch = useCallback(() => {
    controllerRef.current?.abort();
    setResult(undefined);
    setRequest((previousRequest) => ({ ...previousRequest }));
  }, []);

  const state: RequestState<T> = !enabled || !url ? idleState : result?.request === request ? result.state : loadingState;
  return { ...state, refetch };
}

/** Explicit requests via execute(input), with component-local state and no automatic execution or retries. */
export function useRequest<TInput, TData>({ request, reloadOnUnauthorized = false }: IRequestOptions<TInput, TData>) {
  const [state, setState] = useState<RequestState<TData>>(idleState);
  const requestRef = useRef(request);
  const mountedRef = useRef(false);
  const activeRef = useRef<{ controller: AbortController; promise: Promise<RequestResult<TData>> } | undefined>(undefined);

  // Capture the latest committed callback without making callback identity trigger requests.
  useLayoutEffect(() => {
    requestRef.current = request;
  }, [request]);

  useLayoutEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      activeRef.current?.controller.abort();
      activeRef.current = undefined;
    };
  }, []);

  /** Resets state and aborts waiting, not server-side work; execute settles when its callback settles. */
  const cancel = useCallback(() => {
    activeRef.current?.controller.abort();
    activeRef.current = undefined;
    if (mountedRef.current) setState(idleState);
  }, []);

  /** While pending, returns the same promise and ignores new input; cancellation allows a new attempt. */
  const execute = useCallback(
    (input: TInput): Promise<RequestResult<TData>> => {
      if (!mountedRef.current) return Promise.resolve({ status: RequestStatus.cancelled });
      if (activeRef.current) return activeRef.current.promise;
      const controller = new AbortController();
      const sendRequest = requestRef.current;
      setState(loadingState);

      // Defer execution until the active promise is registered, also capturing synchronous callback errors.
      const promise = Promise.resolve().then(async (): Promise<RequestResult<TData>> => {
        try {
          if (controller.signal.aborted) return { status: RequestStatus.cancelled };
          const data = await sendRequest(input, controller.signal);
          if (controller.signal.aborted || !mountedRef.current) return { status: RequestStatus.cancelled };
          setState({ status: RequestStatus.success, data, error: undefined });
          return { status: RequestStatus.success, data };
        } catch (error) {
          if (controller.signal.aborted || !mountedRef.current) return { status: RequestStatus.cancelled };
          const normalizedError = requestErrorFromUnknown(error);
          setState({ status: RequestStatus.failed, data: undefined, error: normalizedError });
          handleUnauthorized(normalizedError, reloadOnUnauthorized);
          return { status: RequestStatus.failed, error: normalizedError };
        } finally {
          // A cancelled attempt must not clear a newer request that has already started.
          if (activeRef.current?.controller === controller) activeRef.current = undefined;
        }
      });

      activeRef.current = { controller, promise };
      return promise;
    },
    [reloadOnUnauthorized],
  );

  return { ...state, execute, cancel };
}
